import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Square,
  SlidersHorizontal,
  Bot,
  Loader2,
  Filter,
  TrendingUp,
  Zap,
  Tag,
  ExternalLink,
} from 'lucide-react';
import { RootState, useAppDispatch } from '../store';
import { screenStocksThunk, stopScreeningThunk } from '../store/thunks';
import {
  openFiltersModal,
  openFinvizScreenerModal,
  openAIAnalysisModal,
} from '../store/slices/modalsSlice';
import { useStocksAndScreening } from '../hooks/useOptimizedSelector';
import { logStateChange } from '../utils/performanceLogger';
import { cn } from '../lib/utils';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';

export type ActionButtonsToolbarVariant = 'default' | 'compact';

export interface ActionButtonsProps {
  /** Compact row + margins on mobile (Charts tab) to maximize chart/list area */
  toolbarVariant?: ActionButtonsToolbarVariant;
}

const ActionButtons: React.FC<ActionButtonsProps> = React.memo(
  ({ toolbarVariant = 'default' }) => {
    const compact = toolbarVariant === 'compact';
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [isStopping, setIsStopping] = useState(false);
    const filters = useSelector((state: RootState) => state.filters);

    // Optimized selector bundle; only status + progress needed here (avoid unused screeningResults under CI eslint)
    const stocksScreening = useStocksAndScreening();
    const screeningStatus = stocksScreening.screeningStatus;
    const screeningProgress = stocksScreening.screeningProgress;

    // Optimized debug logging for status changes
    React.useEffect(() => {
      logStateChange('ActionButtons', {
        status: screeningStatus.status,
        processedCount: screeningProgress.processed_count,
        totalStocks: screeningProgress.total_stocks,
      });
    }, [
      screeningStatus.status,
      screeningProgress.processed_count,
      screeningProgress.total_stocks,
    ]);

    // Helper function to get MA filter display text
    const getMASummary = () => {
      const { ma_filter_selection, ma_filter_mode, ma_timeframe_mode } =
        filters;
      const timeframe = ma_timeframe_mode === 'WEEKLY' ? 'W' : 'D';

      if (ma_filter_selection && Array.isArray(ma_filter_selection)) {
        if (ma_filter_selection.length === 0) return null;
        const mas = ma_filter_selection
          .map((ma) => {
            const match = ma.toString().match(/(\d+)/);
            return match ? match[1] : ma;
          })
          .join('/');
        return `MA ${mas} (${timeframe})`;
      }

      if (ma_filter_mode && ma_filter_mode !== 'NONE') {
        return `MA Filter (${timeframe})`;
      }
      return null;
    };

    // Helper to get active extended filters count
    const getActiveFiltersCount = () => {
      let count = 0;
      if (filters.volume_spike_enabled) count++;
      if (filters.price_action_enabled) count++;
      if (filters.breakout_enabled) count++;
      if (filters.rsi_recovery_enabled) count++;
      if (filters.strong_ma_support_config?.enabled) count++;
      if (filters.strong_support_filter_enabled) count++;
      if (filters.fib_golden_zone_filter_enabled) count++;
      if (filters.darvas_box_filter_enabled) count++;
      if (filters.cup_handle_filter_enabled) count++;
      if (filters.goldenCrossFilterEnabled) count++;
      return count;
    };

    const maSummary = getMASummary();
    const activeFiltersCount = getActiveFiltersCount();

    const handleScreenStocks = useCallback(() => {
      dispatch(screenStocksThunk());
    }, [dispatch]);

    const handleStopScreening = useCallback(async () => {
      setIsStopping(true);
      try {
        await dispatch(stopScreeningThunk()).unwrap();
      } catch (error) {
        console.error('Failed to stop screening:', error);
      } finally {
        // Reset the stopping flag after a short delay to prevent rapid re-clicks
        setTimeout(() => setIsStopping(false), 1000);
      }
    }, [dispatch]);

    const handleOpenFilters = useCallback(() => {
      dispatch(openFiltersModal());
    }, [dispatch]);

    const handleOpenFinvizFilters = useCallback(() => {
      dispatch(openFinvizScreenerModal());
    }, [dispatch]);

    return (
      <div className={cn('shrink-0 mb-0 md:mb-2', compact && 'mb-0 md:mb-1')}>
        {/* Desktop buttons - original layout */}
        <div className="hidden md:flex flex-row items-center gap-2 flex-wrap">
          {/* Conditionally render Screen Stocks or Stop Screening button */}
          {screeningStatus.status === 'running' ? (
            <Button
              variant="danger"
              onClick={handleStopScreening}
              disabled={isStopping}
              leftIcon={
                isStopping ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Square className="h-4 w-4" />
                )
              }
              title={t('screening.stopTitle')}
              className="w-full sm:w-auto"
            >
              {isStopping ? t('screening.stopping') : t('screening.stop')}
            </Button>
          ) : (
            <Button
              variant="success"
              onClick={handleScreenStocks}
              leftIcon={<Search className="h-4 w-4" />}
              title={t('screening.runTitle')}
              className="w-full sm:w-auto"
            >
              {t('screening.runButton')}
            </Button>
          )}

          {/* Configure Filters Button */}
          <Button
            variant="primary"
            onClick={handleOpenFilters}
            leftIcon={<SlidersHorizontal className="h-4 w-4" />}
            title={t('screening.filtersTitle')}
            className="w-full sm:w-auto"
          >
            {t('screening.configureFilters')}
          </Button>
          <Button
            variant="warning"
            onClick={handleOpenFinvizFilters}
            leftIcon={<ExternalLink className="h-4 w-4" />}
            title={t('screening.finvizTitle')}
            className="w-full sm:w-auto"
          >
            {t('filters.finviz.title')}
          </Button>
        </div>

        {/* Mobile action buttons section - flex + gap so status row clears button shadows */}
        <div
          className={cn('md:hidden flex flex-col gap-5', compact && 'gap-2')}
        >
          {/* Mobile action buttons in single row - badges in separate row below */}
          <div
            role="region"
            aria-label={t('screening.actionButtonsToolbar')}
            className="flex w-full min-w-0 flex-row items-stretch gap-1 overflow-hidden touch-manipulation pb-0.5"
          >
            {/* Equal flex + truncate: no horizontal scrollbar on narrow viewports */}
            {screeningStatus.status === 'running' ? (
              <Button
                variant="danger"
                size="sm"
                onClick={handleStopScreening}
                disabled={isStopping}
                leftIcon={
                  isStopping ? (
                    <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                  ) : (
                    <Square className="h-3.5 w-3.5 shrink-0" />
                  )
                }
                title={t('screening.stopTitle')}
                className={cn(
                  'inline-flex items-center justify-center min-w-0 flex-1 basis-0 gap-1 px-1.5 text-xs sm:px-2',
                  compact ? 'min-h-[44px]' : 'min-h-[44px]'
                )}
              >
                <span className="min-w-0 truncate">
                  {isStopping
                    ? t('screening.stoppingShort')
                    : t('screening.stopShort')}
                </span>
              </Button>
            ) : (
              <Button
                variant="success"
                size="sm"
                onClick={handleScreenStocks}
                leftIcon={<Search className="h-3.5 w-3.5 shrink-0" />}
                title={t('screening.runTitle')}
                className={cn(
                  'inline-flex items-center justify-center min-w-0 flex-1 basis-0 gap-1 px-1.5 text-xs sm:px-2',
                  compact ? 'min-h-[44px]' : 'min-h-[44px]'
                )}
              >
                <span className="min-w-0 truncate">
                  {t('screening.runButtonShort')}
                </span>
              </Button>
            )}

            <Button
              variant="primary"
              size="sm"
              onClick={handleOpenFilters}
              leftIcon={<SlidersHorizontal className="h-3.5 w-3.5 shrink-0" />}
              title={t('screening.filtersTitle')}
              className={cn(
                'inline-flex items-center justify-center min-w-0 flex-1 basis-0 gap-1 px-1.5 text-xs sm:px-2',
                compact ? 'min-h-[44px]' : 'min-h-[44px]'
              )}
            >
              <span className="min-w-0 truncate">
                {t('screening.configureFiltersShort')}
              </span>
            </Button>

            <Button
              variant="warning"
              size="sm"
              onClick={handleOpenFinvizFilters}
              leftIcon={<ExternalLink className="h-3.5 w-3.5 shrink-0" />}
              title={t('screening.finvizTitle')}
              className={cn(
                'inline-flex items-center justify-center min-w-0 flex-1 basis-0 gap-1 px-1.5 text-xs sm:px-2',
                compact ? 'min-h-[44px]' : 'min-h-[44px]'
              )}
            >
              <span className="min-w-0 truncate">
                <span className="sm:hidden">
                  {t('screening.advancedShort', 'Advanced')}
                </span>
                <span className="hidden sm:inline">
                  {t('screening.advancedFiltersShort')}
                </span>
              </span>
            </Button>
          </div>

          {/* Mobile Screening Status Indicator - separate row */}
          {screeningStatus.status === 'running' && (
            <Badge
              variant="bullish"
              size="lg"
              icon={<Loader2 className="h-3 w-3 animate-spin" />}
              className="w-full justify-center shrink-0"
            >
              {screeningStatus.total_stocks === 0
                ? screeningStatus.message || t('screening.preparing')
                : t('screening.processingWithTotal', {
                    processed: screeningStatus.processed_count,
                    total: screeningStatus.total_stocks,
                  })}
            </Badge>
          )}

          {/* Completed count: omitted on mobile - same info appears under "Screening Results" to save vertical space */}
        </div>

        {/* AI Analysis Button - currently disabled for all resolutions */}
        {false && (
          <Button
            variant="warning"
            onClick={() => dispatch(openAIAnalysisModal())}
            leftIcon={<Bot className="h-4 w-4" />}
            title="Access AI-powered analysis tools"
            className="w-full sm:w-auto"
          >
            AI Analysis
          </Button>
        )}

        {/* Screening Status Indicator */}
        {screeningStatus.status === 'running' && (
          <Badge
            variant="bullish"
            size="lg"
            icon={<Loader2 className="h-3 w-3 animate-spin" />}
            className="ml-2 hidden md:inline-flex"
          >
            {screeningStatus.total_stocks === 0
              ? screeningStatus.message || t('screening.preparing')
              : t('screening.processingWithTotal', {
                  processed: screeningStatus.processed_count,
                  total: screeningStatus.total_stocks,
                })}
          </Badge>
        )}

        {/* Completed count: omitted - Screening Results / Charts headers already show counts */}

        {/* Separator */}
        {(maSummary || activeFiltersCount > 0) && (
          <div className="h-px w-full sm:h-6 sm:w-px bg-border-subtle sm:mx-1 hidden md:block" />
        )}

        {/* Active Filters Summary */}
        {(maSummary || activeFiltersCount > 0) && (
          <div className="hidden md:flex items-center gap-1.5 flex-wrap w-full sm:w-auto">
            <Filter className="h-3.5 w-3.5 text-text-secondary" />
            {maSummary && (
              <Badge
                variant="default"
                size="sm"
                icon={<TrendingUp className="h-3 w-3" />}
              >
                {maSummary}
              </Badge>
            )}
            {activeFiltersCount > 0 && (
              <Badge
                variant="bullish"
                size="sm"
                icon={<Zap className="h-3 w-3" />}
              >
                +{activeFiltersCount} filters
              </Badge>
            )}
            {filters.active_category &&
              filters.active_category !== 'custom' && (
                <Badge
                  variant="warning"
                  size="sm"
                  icon={<Tag className="h-3 w-3" />}
                >
                  {filters.active_category}
                </Badge>
              )}
          </div>
        )}
      </div>
    );
  }
);

ActionButtons.displayName = 'ActionButtons';

export default ActionButtons;
